"use client"
import {
  type Client,
  graphql,
  type ItemIdentifier,
  prefetch,
  type VariablesOf,
} from "@opensea/graphql"
import { HandoffProvider, usePathname } from "@opensea/navigation"
import {
  EmptyState,
  Grid,
  useIsGreaterThanOrEqualToLg,
  VirtualizedTable,
} from "@opensea/ui-kit"
import { useSelectItemKeyboardShortcuts } from "@opensea/ui-kit/hooks"
import { type Address, normalizeAddress } from "@opensea/utils"
import { useFlowEventHandlers } from "@opensea/wallet"
import { useEffect } from "react"
import { useContextSelector } from "use-context-selector"
import { useShallow } from "zustand/react/shallow"
import { FilterLayoutContext } from "@/components/layout/FilterLayoutProvider"
import {
  useIsOwnProfileAddress,
  useProfileAddress,
} from "@/components/profile/ProfileAddressProvider"
import { withSuspense } from "@/components/suspense/WithSuspense"
import { usePaginatedQuery } from "@/hooks/api/use-paginated-query"
import { useProfileItemsSubscription } from "@/hooks/api/use-profile-items-subscription"
import { profileItemPaginatorFragment } from "@/hooks/api/use-profile-items-subscription.fragment"
import { useClearFilters } from "@/hooks/query-param/use-clear-filters"
import { useProfileItemsSortByQueryParam } from "@/hooks/query-param/use-profile-items-sort-by-query-param"
import { useSortDirectionQueryParam } from "@/hooks/query-param/use-sort-direction-query-param"
import { useHasSearchParams } from "@/hooks/use-has-search-params"
import { usePreserveValuePriorToItemPage } from "@/hooks/use-preserve-value-prior-to-item-page"
import { useSetAllItems } from "@/hooks/use-set-all-items"
import type { InitialRect } from "@/lib/virtualization"
import { ItemsEmptyState } from "../ItemsEmptyState"
import { ItemsMediaContextProvider } from "../ItemsMediaContext"
import { useItemView } from "../ItemViewProvider"
import { ItemOwnedQuantityFragment } from "../utils"
import { PAGE_SIZE, PAGINATION_PAGE_SIZE } from "./constants"
import { ProfileItemsCard, ProfileItemsCardFragment } from "./ProfileItemsGrid"
import { ProfileItemsListSkeleton } from "./ProfileItemsListSkeleton"
import { useProfileItemsSelectionStore } from "./ProfileItemsSelectionProvider"
import {
  ProfileItemsTableHeader,
  ProfileItemsTableRow,
  ProfileItemsTableRowFragment,
} from "./ProfileItemsTable"
import { profileItemsSelectionFragment } from "./profile-items-selection-store"
import { useProfileItemsFilters } from "./use-profile-items-filter"

type Props = {
  initialRect?: InitialRect
}

const query = graphql(
  `
    query ProfileItemsListQuery(
      $address: Address!
      $limit: Int
      $cursor: String
      $sort: ProfileItemsSort!
      $filter: ProfileItemsFilter
    ) {
      profileItems(
        addresses: [$address]
        limit: $limit
        sort: $sort
        filter: $filter
        cursor: $cursor
      ) {
        nextPageCursor
        items {
          version
          id
          collection {
            id
            slug
          }
          enforcement {
            isDelisted
          }
          isFungible
          tokenId
          contractAddress
          ...ItemOwnedQuantity
          lastTransferAt
          ownership(address: $address) {
            id
            isHidden
          }
          chain {
            identifier
          }
          bestListing {
            startTime
            maker {
              address
            }
          }
          bestOffer {
            pricePerItem {
              usd
            }
          }
          lowestListingForOwner(address: $address) {
            pricePerItem {
              usd
            }
          }
          ...ProfileItemsTableRowFragment
          ...ProfileItemsCardFragment
          ...profileItemsSelection
          ...ProfileItemPaginator
        }
      }
    }
  `,
  [
    ProfileItemsTableRowFragment,
    ProfileItemsCardFragment,
    profileItemsSelectionFragment,
    profileItemPaginatorFragment,
    ItemOwnedQuantityFragment,
  ],
)

function ProfileItemsListWrapper({ initialRect }: Props) {
  const address = useProfileAddress()
  const { sortBy } = useProfileItemsSortByQueryParam()
  const { sortDirection } = useSortDirectionQueryParam("DESC")
  const filter = useProfileItemsFilters()

  const variables = {
    address,
    limit: PAGE_SIZE,
    sort: { by: sortBy, direction: sortDirection },
    filter,
  }

  const preservedVariables = usePreserveValuePriorToItemPage(variables)

  return (
    <ProfileItemsList
      initialRect={initialRect}
      key={JSON.stringify(preservedVariables)}
      variables={preservedVariables}
    />
  )
}

const ProfileItemsList = withSuspense(
  ({
    variables,
    initialRect,
  }: {
    variables: VariablesOf<typeof query>
    initialRect?: InitialRect
  }) => {
    const { itemView, itemViewGroup, size } = useItemView()
    const address = useProfileAddress()
    const pathname = usePathname()
    const { sortBy } = useProfileItemsSortByQueryParam()
    const { sortDirection } = useSortDirectionQueryParam("DESC")
    const [
      setAllItems,
      resetSelections,
      selectAllItems,
      setHasMoreItems,
      setIsLoadingItems,
    ] = useProfileItemsSelectionStore(
      useShallow(v => [
        v.setAllItems,
        v.resetSelections,
        v.selectAllItems,
        v.setHasMoreItems,
        v.setIsLoadingItems,
      ]),
    )

    const [isSideModuleOpen, isFilterSidePanelOpen] = useContextSelector(
      FilterLayoutContext,
      v => [v.isSideModuleOpen, v.isFilterSidePanelOpen],
    )

    const isOwnProfileAddress = useIsOwnProfileAddress()

    useSelectItemKeyboardShortcuts({
      selectAllItems,
      enabled: isOwnProfileAddress,
    })

    const { items, pagination, setItems, noResults, refetch } =
      usePaginatedQuery(
        {
          query,
          variables,
          pause: !address,
        },
        v => v?.profileItems,
        {
          pageSize: PAGE_SIZE,
          paginationPageSize: PAGINATION_PAGE_SIZE,
        },
      )

    useEffect(() => {
      setIsLoadingItems(false)
    }, [setIsLoadingItems])

    useFlowEventHandlers({
      onSuccess: () => {
        resetSelections()
        refetch()
      },
    })

    function getCollectionData(): [string[], ItemIdentifier[]] {
      const ids = new Set<string>()
      const fungibleItems: Record<string, ItemIdentifier> = {}
      for (const item of items) {
        if (item?.collection?.slug) {
          ids.add(item.collection.slug)
        }
        if (item?.isFungible) {
          fungibleItems[item.id] = {
            chain: item.chain.identifier,
            tokenId: item.tokenId,
            contractAddress: item.contractAddress,
          }
        }
      }

      return [Array.from(ids), Object.values(fungibleItems)]
    }

    const [collectionSlugs, fungibleItemIdentifiers] = getCollectionData()

    useProfileItemsSubscription({
      collectionSlugs,
      fungibleItemIdentifiers,
      address,
      sortBy,
      sortDirection,
      setItems,
      pagination,
      filter: variables.filter,
    })

    useSetAllItems({ items, setAllItems })
    useEffect(() => {
      setHasMoreItems(pagination.hasNext)
    }, [pagination.hasNext, setHasMoreItems])

    const clearFilters = useClearFilters()
    const hasSearchParams = useHasSearchParams()
    const isGreaterThanOrEqualToLg = useIsGreaterThanOrEqualToLg()

    // For top offer sort, we need to re-sort on client as we use collection, item, trait on BE
    function getSortedItems() {
      const isTopOfferSort = sortBy === "TOP_OFFER"
      if (isTopOfferSort) {
        return items.sort((a, b) => {
          return sortDirection === "DESC"
            ? (b?.bestOffer?.pricePerItem.usd || Number.NEGATIVE_INFINITY) -
                (a?.bestOffer?.pricePerItem.usd || Number.NEGATIVE_INFINITY)
            : (a?.bestOffer?.pricePerItem.usd || Number.POSITIVE_INFINITY) -
                (b?.bestOffer?.pricePerItem.usd || Number.POSITIVE_INFINITY)
        })
      }
      const isBestListingSort = sortBy === "LISTING_OWN_PRICE"
      if (isBestListingSort) {
        return items.sort((a, b) => {
          return sortDirection === "DESC"
            ? (b?.lowestListingForOwner?.pricePerItem.usd ??
                Number.NEGATIVE_INFINITY) -
                (a?.lowestListingForOwner?.pricePerItem.usd ??
                  Number.NEGATIVE_INFINITY)
            : (a?.lowestListingForOwner?.pricePerItem.usd ??
                Number.POSITIVE_INFINITY) -
                (b?.lowestListingForOwner?.pricePerItem.usd ??
                  Number.POSITIVE_INFINITY)
        })
      }
      return items
    }
    const sortedItems = getSortedItems()

    if (!address) {
      return <ProfileItemsListSkeleton />
    }

    if (noResults && !pagination.hasNext) {
      return (
        <ItemsEmptyState
          hasSearchParams={hasSearchParams}
          onResetFilters={clearFilters}
        />
      )
    }

    // We only want to update the items store when we're on the profile page.
    // There's a side effect when the search params change when navigating to the item quick view
    // And since we can't conditionally enable useSuspenseQuery like we can with useQuery,
    // we have to enable/disable the store mutator instead.
    // see https://github.com/TanStack/query/discussions/6327
    // see https://linear.app/opensea/issue/WEB-261/when-item-page-changes-the-url-the-whole-items-list-in-the-background
    const isHandoffEnabled = !pathname.includes("/item/")

    if (itemViewGroup === "table") {
      const renderKey = JSON.stringify({ ...variables, itemView })

      return (
        <HandoffProvider enabled={isHandoffEnabled} handoff={sortedItems}>
          <VirtualizedTable
            className="pb-4"
            header={<ProfileItemsTableHeader />}
            headerBorderOnScroll={isGreaterThanOrEqualToLg}
            initialRect={initialRect}
            itemKey={item => item?.id}
            items={sortedItems}
            key={renderKey}
            renderRow={ProfileItemsTableRow}
            size={size}
            {...pagination}
          />
        </HandoffProvider>
      )
    }

    return (
      <HandoffProvider enabled={isHandoffEnabled} handoff={sortedItems}>
        <ItemsMediaContextProvider>
          <Grid
            className="pb-4"
            itemKey={item => item?.id}
            items={sortedItems}
            key={`${JSON.stringify(variables)}-${isFilterSidePanelOpen.toString()}-${isSideModuleOpen.toString()}${itemView === "mosaic" ? pathname : ""}`}
            renderItem={ProfileItemsCard}
            size={size}
            variant={itemView === "mosaic" ? "gallery" : "default"}
            {...pagination}
          />
        </ItemsMediaContextProvider>
      </HandoffProvider>
    )
  },
  {
    fallback: <ProfileItemsListSkeleton />,
    errorFallback: <EmptyState size="lg" variant="no-data" />,
    ssr: false,
  },
)

export { ProfileItemsListWrapper as ProfileItemsList }

export const prefetchProfileItems = (address: Address, client: Client) => {
  prefetch(client, query, {
    address: normalizeAddress(address),
    limit: PAGE_SIZE,
    sort: { by: "RECEIVED_DATE", direction: "DESC" },
  })
}
